import { addDays, format, parse } from "date-fns";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DateCalendar,
  DatePicker,
  DesktopTimePicker,
  Grid,
  Loading,
  LocalizationProvider,
  TextField,
  Typography,
} from "../../../components";
import { value } from "../../../helpers";

export default function AgendamentoForm({
  agendamento,
  setAgendamento,
  configuracoes,
  clienteList,
  servicoList,
  horariosList,
  dataSelecionada,
  setMesSelecionado,
  onChangeDate,
  shouldDisableDate,
  disableMesAnterior,
  disableMesPosterior,
  handleChangeTextfield,
  handleSubmit,
  mensagemErro,
  empresaLabel,
  loading,
}) {
  const footerHeight = 64;

  return (
    <Box
      className="bg-white w-100 d-flex flex-column justify-content-center align-items-center"
      sx={{
        minHeight: "100dvh",
        boxSizing: "border-box",
      }}
    >
      {mensagemErro ? (
        <Card className="mb-4 rounded-4">
          <CardContent>
            <Typography
              sx={{ fontWeight: "bold", fontSize: "15px" }}
              className="mb-2"
            >
              Agendamento Indisponível
            </Typography>
            <Typography variant="body1" color="textSecondary" className="mb-3">
              {mensagemErro}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <div className="d-flex flex-row align-items-stretch gap-3 mt-3 mx-4 w-100">
          <div className="mx-4" style={{ minHeight: "450px", flex: 2 }}>
            <DateCalendar
              value={dataSelecionada}
              shouldDisableDate={shouldDisableDate}
              onMonthChange={(value) =>
                setMesSelecionado(new Date(value).toISOString())
              }
              onChange={(value) => onChangeDate("data", value)}
              minDate={new Date()}
              maxDate={addDays(new Date(), 30)}
              disabledPrevMonth={disableMesAnterior()}
              disabledNextMonth={disableMesPosterior()}
            />
          </div>
          <div
            className="me-5 d-flex flex-column justify-content-center"
            style={{ flex: 1 }}
          >
            <div>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="clienteId"
                    name="clienteId"
                    options={clienteList}
                    autoHighlight
                    disableClearable
                    getOptionLabel={(option) => option.clienteNome}
                    loadingText="Carregando"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={"Cliente"}
                        variant="filled"
                        disabled
                      />
                    )}
                    value={value.autoComplete(
                      clienteList,
                      agendamento?.clienteId,
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    id="servicoId"
                    name="servicoId"
                    options={servicoList}
                    autoHighlight
                    disableClearable
                    getOptionLabel={(option) => option.servicoNome}
                    value={value.autoComplete(
                      servicoList,
                      agendamento?.servicoId,
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={"Serviço"}
                        variant={"filled"}
                        disabled
                      />
                    )}
                  />
                </Grid>
                {configuracoes.intervaloAgendamentos !== "null" ? (
                  <Grid item xs={12}>
                    <div className="d-flex flex-row gap-3 my-2 flex-wrap">
                      {horariosList.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          clickable
                          onClick={() =>
                            setAgendamento((prev) => ({
                              ...prev,
                              hora: item,
                            }))
                          }
                          color={
                            agendamento?.hora === item ? "primary" : "default"
                          }
                          variant={
                            agendamento?.hora === item ? "filled" : "outlined"
                          }
                          sx={{
                            fontSize: "14px",
                            padding: "8px",
                            "& .MuiChip-label": {
                              px: 1,
                              whiteSpace: "nowrap",
                            },
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              boxShadow: 1,
                            },
                          }}
                        />
                      ))}
                    </div>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={6}>
                      <DatePicker
                        id="data"
                        name="data"
                        label="Data"
                        format="dd/MM/yyyy"
                        disabled
                        value={value.date(dataSelecionada)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <LocalizationProvider>
                        <DesktopTimePicker
                          sx={{ width: "100%" }}
                          name="hora"
                          label="Hora"
                          disabled={!agendamento?.data}
                          onChange={(time) => {
                            if (!time) return;
                            setAgendamento((prev) => ({
                              ...prev,
                              hora:
                                time != "Invalid Date"
                                  ? format(time, "HH:mm")
                                  : null,
                            }));
                          }}
                          value={
                            agendamento?.hora
                              ? parse(agendamento.hora, "HH:mm", new Date())
                              : null
                          }
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField
                    id="observacao"
                    name="observacao"
                    label="Observação"
                    variant="outlined"
                    value={agendamento?.observacao ?? ""}
                    onChange={handleChangeTextfield}
                    InputProps={{ inputProps: { maxLength: 50 } }}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained"
                className="fw-bold rounded-3 mt-3"
                disableElevation
                fullWidth
                color="primary"
                onClick={handleSubmit}
                disabled={!agendamento.data || !agendamento.hora}
              >
                Confirmar Agendamento
              </Button>
            </div>
          </div>
        </div>
      )}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          top: footerHeight,
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1200,
          px: 2,
        }}
      >
        <Typography
          color="primary"
          variant="h1"
          className="text-uppercase fw-bolder"
          sx={{
            fontSize: {
              xs: 25,
              md: 40,
            },
            lineHeight: 1.2,
          }}
        >
          Agendamento de Serviço
        </Typography>
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: footerHeight,
          backgroundColor: "#fff",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1200,
          px: 2,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {empresaLabel}
        </Typography>
      </Box>
      <Loading loading={loading} />
    </Box>
  );
}
